import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { contains } from '@library/common/functions';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';

import Fuse from 'fuse.js';
import { IGenericType } from '@library/common/generictype';

@UntilDestroy()
@Injectable({
   providedIn: 'root',
})
export class SearchService {
   params = new FormGroup({
      shipType: new FormControl(),
      mode: new FormControl(),
      text: new FormControl(),
   })

   get text() {
      return this.params.get( 'text' ) as FormControl;
   }
   set text( value: FormControl ) {
      this.params.get( 'text' ).setValue( value );
   }

   _showSearch = false;
   get showSearch() { return this._showSearch; }
   set showSearch( value ) {
      this._showSearch = value;
      this.reset();

      if ( value ) {
         this.ks.register$({
            description: 'Search',
            key: 'S',
            event: e => {
               if ( !this.showSearch ) { return false; }

               if ( e.shiftKey && e.key === 'S' ) { return true; }

               if (( e.metaKey || e.ctrlKey ) && e.key === 'f' ) {
                  console.log( e );
                  e.preventDefault();

                  return true;
               }
            },
            keyDisplay: 'CMD + F (Mac) OR CTRL + F (PC)',
            icon: 'search'
         }).pipe(
            filter( _ => this.showSearch ),
            untilDestroyed( this ),
         ).subscribe( _ => this.searchActive$.next( true ));

         this.ks.escape$.pipe( untilDestroyed( this )).subscribe( _ => {
            this.softReset();
         });
      } else {
         this.ks.remove( 'S' );
      }
   }

   searchActive$ = new BehaviorSubject( false );
   blurText = new BehaviorSubject( false );
   enterKeyUp = new BehaviorSubject( false );

   constructor(
      private ks: KeyboardShortcutsService,
      private router: Router,
   ) {
      this.router.events.pipe(
         filter( event => event instanceof NavigationEnd ),
         untilDestroyed( this ),
      ).subscribe( _ => this.softReset() );
   }

   empty(): boolean {
      return !Object.keys( this.params.value )
         .map( key => this.params.value[key] )
         .filter( v => v ).length;
   }

   softReset() {
      this.params.reset();
      this.searchActive$.next( false );
   }

   reset(): void {
      this.params.reset();
      this.searchActive$.next( false );
      this.ks.remove( 'S' );
   }

   objectToString( object: object ): string {
      if ( !( object instanceof Object )) { return object; }

      return Object.keys( object ).reduce(( current: string, key: string ) => {
         if ( typeof( object[key] ) === 'boolean' ) { return current; }
         if ( current.length ) { current += '◬'; }

         return current + this.objectToString( object[key] );
      }, '' ).toLowerCase();
   }

   textMatches( value: string ): boolean {
      return contains( value, this.text.value );
   }

   searchList<T>( list: T[], keys?: IGenericType[] ) {
      let ret = list;

      if (keys == null && list.length > 0) {
         keys = Object.keys( list[0] );
      }

      list.forEach((l: any) => {
         l.objStr = this.objectToString( l );
      });

      const f = new Fuse( list, {
         isCaseSensitive: false,
         keys: [{ name: 'objStr', weight: 0.5 }].concat( keys ),
         includeScore: true,
      } );

      if ( this.text.value != null && this.text.value !== '' ) {
         ret = f.search( this.text.value ).map((i) => i.item);
      }

      return ret;
   }

   blur() {
      this.blurText.next( true );
   }
}
